import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { DecisionQueueRow, DecisionQueueView, GuaranteeDecision } from '@lib/guarantees/data-access';
import { selectRefreshingGuaranteeIds } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { openDecisionForm } from '../../operations/decision-form.component';
import { ChipComponent } from '../common/chip.component';
import { DiagnosisChipComponent } from '../common/diagnosis-chip.component';
import { SERVICE_KIND_ICON, SERVICE_KIND_LABEL_KEY, SEVERITY_TONE } from '../common/guarantee-vocabulary';
import { DecisionAnswer } from '../common/prescription-decisions';

/**
 * What the loaded window still owes an answer on, as a flat work list rather than a care plan.
 * The stream already shows these lines in their clinical context; this shows them in the order
 * they should be worked — longest wait first — and nothing else.
 *
 * The list is split by whether a ruling is possible at all. A line with no diagnosis is refused by
 * the backend, so those are held apart: mixing them in leaves a health manager working downwards
 * into rows that cannot be acted on, which is exactly what the decision facet does today.
 */
@Component({
  selector: 'guarantees-decision-queue',
  standalone: true,
  imports: [DatePipe, MatIcon, MatTooltip, TranslatePipe, ChipComponent, DiagnosisChipComponent],
  templateUrl: './decision-queue.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DecisionQueueComponent {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly refreshingIds = this.store.selectSignal(selectRefreshingGuaranteeIds);

  queue = input.required<DecisionQueueView>();

  /** Withholds every control that writes, for a portal that may read the record but not change it. */
  readOnly = input<boolean>(false);

  /** The queue does not own navigation: opening a row hands the visit back to the page. */
  guaranteeOpened = output<string>();

  /** Exposed rather than written as literals in the markup — the template cannot see the enum. */
  protected readonly decisions = GuaranteeDecision;

  protected readonly kindIcons = SERVICE_KIND_ICON;
  protected readonly kindLabelKeys = SERVICE_KIND_LABEL_KEY;
  protected readonly severityTones = SEVERITY_TONE;

  protected readonly total = computed(() => this.queue().ready.length + this.queue().blocked.length);

  /**
   * Lines awaiting a ruling across both sections. Counting only the rulable ones would report the
   * blocked work as though it were not owed an answer, which is the reading this split exists to
   * prevent — the section headers are what say how much of it can be acted on right now.
   */
  protected readonly pendingCount = computed(() => [...this.queue().ready, ...this.queue().blocked].reduce((total, row) => total + row.pendingLines.length, 0));

  /**
   * A write is already in flight on the row's record, so its answers are held back until it lands —
   * a ruling clicked twice while the first is still writing would be a second ruling, not a retry.
   */
  protected busy(guaranteeId: string): boolean {
    return this.refreshingIds().includes(guaranteeId);
  }

  /**
   * Ruling from the work list, with the answer already chosen — which is what makes this a triage
   * screen rather than a second care plan. The flow still opens, because the grounds are required
   * and the row is not the place to pick from sixty-two of them.
   *
   * Opened on the undecided members alone: the row exists because those are still owed an answer,
   * and re-opening the ones already settled is the stream's job, not the queue's.
   */
  protected rule(row: DecisionQueueRow, decision: DecisionAnswer): void {
    openDecisionForm(this.dialog, this.store, {
      guaranteeId: row.guaranteeId,
      lines: row.pendingLines,
      titleKey: row.set.isSet ? 'guaranteeV2.action.setDecision' : 'guaranteeV2.action.lineDecision',
      decision,
    });
  }
}
